import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { fetchDocuments, type DocDocument } from '@/lib/documents';
import { docHasText, extractDocText } from '@/lib/docText';
import { localGet, localId } from '@/lib/appStorage';
import { splitSentences, type NotesState } from '@/pages/client/apps/notes/notesUtils';
import { makeCard, type Card, type Deck } from '../types';

interface Props {
  userId: number;
  t: (key: string) => string;
  onClose: () => void;
  onCreate: (deck: Deck) => void;
}

interface SourceNote {
  id: string;
  title: string;
  notebook: string;
  text: string;
}

interface DraftCard {
  id: string;
  question: string;
  answer: string;
  explanation: string;
}

function parseCards(raw: string): DraftCard[] {
  const cleaned = raw
    .replace(/```json/gi, '')
    .replace(/```/g, '')
    .trim();
  const start = cleaned.indexOf('[');
  const end = cleaned.lastIndexOf(']');
  if (start === -1 || end === -1 || end <= start) return [];
  try {
    const parsed = JSON.parse(cleaned.slice(start, end + 1)) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((item) => {
        const row = item as { question?: string; answer?: string; explanation?: string };
        const question = String(row?.question || '').trim();
        const answer = String(row?.answer || '').trim();
        if (!question || !answer) return null;
        return {
          id: localId('draft'),
          question,
          answer,
          explanation: String(row?.explanation || '').trim(),
        };
      })
      .filter((row): row is DraftCard => row !== null)
      .slice(0, 15);
  } catch {
    return [];
  }
}

function localDraft(text: string, title: string): DraftCard[] {
  return splitSentences(text)
    .slice(0, 8)
    .map((sentence) => ({
      id: localId('draft'),
      question: `Que retenir de « ${title} » ?`,
      answer: sentence,
      explanation: '',
    }));
}

/**
 * Création d'un quiz par IA à partir :
 *  • des notes de l'utilisateur (Zifek Notes), ou
 *  • d'un document personnel (Word, Excel, PowerPoint, PDF texte).
 * Les questions générées sont TOUJOURS relues avant d'être utilisées.
 */
export default function GeneratorModal({ userId, t, onClose, onCreate }: Props) {
  const [tab, setTab] = useState<'notes' | 'doc'>('notes');
  const [notes, setNotes] = useState<SourceNote[]>([]);
  const [docs, setDocs] = useState<DocDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedNotes, setSelectedNotes] = useState<string[]>([]);
  const [selectedDoc, setSelectedDoc] = useState<number | null>(null);
  const [generating, setGenerating] = useState(false);
  const [drafts, setDrafts] = useState<DraftCard[]>([]);
  const [sourceLabel, setSourceLabel] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [usedFallback, setUsedFallback] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const state = await localGet<NotesState>(`user::${userId}:client_notes_v2`);
      if (state) {
        const notebookName = (id: string) => state.notebooks.find((nb) => nb.id === id)?.name || '';
        setNotes(
          state.notes
            .filter((note) => note.content.trim().length > 60)
            .map((note) => ({
              id: note.id,
              title: note.title || t('notes.untitled'),
              notebook: notebookName(note.notebookId),
              text: `${note.title}\n${note.content}`,
            })),
        );
      }
      const documents = await fetchDocuments(userId);
      setDocs(documents.filter((doc) => docHasText(doc)));
    } catch {
      setError('Impossible de charger vos sources.');
    } finally {
      setLoading(false);
    }
  }, [userId, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const { sourceText, currentLabel } = useMemo(() => {
    if (tab === 'notes') {
      const picked = notes.filter((note) => selectedNotes.includes(note.id));
      const label = picked.length === 1 ? picked[0].title : picked.length > 1 ? `${picked.length} notes` : '';
      return {
        sourceText: picked.map((note) => note.text).join('\n\n').slice(0, 8000),
        currentLabel: label,
      };
    }
    const doc = docs.find((item) => item.id === selectedDoc);
    return {
      sourceText: doc ? extractDocText(doc) : '',
      currentLabel: doc ? doc.name : '',
    };
  }, [tab, notes, docs, selectedNotes, selectedDoc]);

  const generate = async () => {
    if (!sourceText.trim()) {
      setError('Choisissez au moins une source avec du contenu.');
      return;
    }
    setGenerating(true);
    setError(null);
    setUsedFallback(false);
    setDrafts([]);
    const labelValue = currentLabel || 'Mes cours';
    setSourceLabel(labelValue);
    try {
      const prompt = `À partir du contenu de cours ci-dessous, crée 8 questions de révision courtes et variées (définitions, compréhension, application).
Chaque question doit avoir une réponse brève (1 à 2 phrases) et une explication facultative.
Réponds UNIQUEMENT par un tableau JSON valide, sans aucun texte autour, au format :
[{"question":"...","answer":"...","explanation":"..."}]

CONTENU :
---
${sourceText}
---`;
      const { data, error: fnError } = await supabase.functions.invoke('ollama-cloud', {
        body: {
          messages: [
            {
              role: 'system',
              content:
                "Tu es un professeur qui prépare des quiz de révision. Tu réponds toujours en français, uniquement avec un tableau JSON valide.",
            },
            { role: 'user', content: prompt },
          ],
        },
      });
      if (fnError) throw new Error(fnError.message || 'IA indisponible');
      const reply = (data as { reply?: string } | null)?.reply || '';
      const parsed = parseCards(reply);
      if (parsed.length >= 3) {
        setDrafts(parsed);
      } else {
        setDrafts(localDraft(sourceText, labelValue));
        setUsedFallback(true);
      }
    } catch {
      setDrafts(localDraft(sourceText, labelValue));
      setUsedFallback(true);
    } finally {
      setGenerating(false);
    }
  };

  const removeDraft = (id: string) => setDrafts((prev) => prev.filter((item) => item.id !== id));

  const createDeck = () => {
    if (drafts.length === 0) return;
    const cards: Card[] = drafts.map((draft) => makeCard(draft.question, draft.answer, draft.explanation));
    onCreate({
      id: localId('deck'),
      name: sourceLabel || 'Nouveau jeu',
      subject: tab === 'doc' ? 'Document' : 'Mes notes',
      chapter: tab === 'doc' ? 'Généré depuis un document' : 'Généré depuis mes notes',
      cards,
      origin: tab === 'doc' ? 'document' : 'notes',
    });
  };

  const toggleNote = (id: string) => {
    setSelectedNotes((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="absolute inset-0 bg-foreground-950/50" onClick={onClose} aria-hidden="true" />
      <div className="relative w-full sm:max-w-2xl max-h-[92vh] overflow-y-auto bg-background-50 rounded-t-2xl sm:rounded-xl p-4 sm:p-5">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-center gap-2 min-w-0">
            <span className="w-9 h-9 rounded-lg bg-accent-100 text-accent-800 flex items-center justify-center flex-shrink-0">
              <i className="ri-sparkling-line"></i>
            </span>
            <div className="min-w-0">
              <h3 className="text-sm font-bold font-heading text-foreground-950">{t('cards.generateTitle')}</h3>
              <p className="text-[11px] text-foreground-500">{t('cards.subtitle')}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full flex items-center justify-center text-foreground-500 hover:bg-background-100 cursor-pointer flex-shrink-0"
            title={t('app.close')}
          >
            <i className="ri-close-line"></i>
          </button>
        </div>

        <div className="flex items-center gap-0.5 bg-background-100 rounded-full p-1 w-fit mb-3">
          <button
            onClick={() => {
              setTab('notes');
              setDrafts([]);
            }}
            className={`px-3 py-1.5 rounded-full text-[11px] font-medium whitespace-nowrap cursor-pointer ${
              tab === 'notes' ? 'bg-background-50 text-foreground-900' : 'text-foreground-500'
            }`}
          >
            <i className="ri-sticky-note-line mr-1"></i>
            {t('cards.sourceNotes')}
          </button>
          <button
            onClick={() => {
              setTab('doc');
              setDrafts([]);
            }}
            className={`px-3 py-1.5 rounded-full text-[11px] font-medium whitespace-nowrap cursor-pointer ${
              tab === 'doc' ? 'bg-background-50 text-foreground-900' : 'text-foreground-500'
            }`}
          >
            <i className="ri-file-text-line mr-1"></i>
            {t('cards.sourceDocs')}
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-10">
            <i className="ri-loader-4-line animate-spin text-xl text-foreground-400"></i>
          </div>
        ) : (
          <>
            {tab === 'notes' ? (
              <div className="max-h-56 overflow-y-auto rounded-lg border border-background-200/60 divide-y divide-background-200/40">
                {notes.length === 0 && (
                  <p className="text-[11px] text-foreground-400 px-3 py-4">
                    Aucune note exploitable. Créez des notes dans Zifek Notes.
                  </p>
                )}
                {notes.map((note) => (
                  <label
                    key={note.id}
                    className={`flex items-start gap-2 px-3 py-2.5 cursor-pointer ${
                      selectedNotes.includes(note.id) ? 'bg-accent-50/70' : 'hover:bg-background-100/60'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedNotes.includes(note.id)}
                      onChange={() => toggleNote(note.id)}
                      className="mt-0.5 w-3.5 h-3.5 accent-accent-600 cursor-pointer"
                    />
                    <span className="min-w-0">
                      <span className="block text-[11px] font-medium text-foreground-800 truncate">{note.title}</span>
                      <span className="block text-[10px] text-foreground-400 truncate">{note.notebook}</span>
                    </span>
                  </label>
                ))}
              </div>
            ) : (
              <div className="max-h-56 overflow-y-auto rounded-lg border border-background-200/60 divide-y divide-background-200/40">
                {docs.length === 0 && (
                  <p className="text-[11px] text-foreground-400 px-3 py-4">
                    Aucun document avec du texte. Importez un fichier Word, Excel ou PowerPoint dans Zifek Documents.
                  </p>
                )}
                {docs.map((doc) => (
                  <label
                    key={doc.id}
                    className={`flex items-center gap-2 px-3 py-2.5 cursor-pointer ${
                      selectedDoc === doc.id ? 'bg-accent-50/70' : 'hover:bg-background-100/60'
                    }`}
                  >
                    <input
                      type="radio"
                      name="doc-source"
                      checked={selectedDoc === doc.id}
                      onChange={() => setSelectedDoc(doc.id)}
                      className="w-3.5 h-3.5 accent-accent-600 cursor-pointer"
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block text-[11px] font-medium text-foreground-800 truncate">{doc.name}</span>
                      <span className="block text-[10px] text-foreground-400 uppercase">{doc.doc_type}</span>
                    </span>
                    <i className="ri-file-text-line text-foreground-400 text-sm"></i>
                  </label>
                ))}
              </div>
            )}

            {error && (
              <p className="text-[11px] text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2 mt-3">{error}</p>
            )}

            {usedFallback && drafts.length > 0 && (
              <p className="text-[11px] text-accent-900 bg-accent-100/70 rounded-md px-3 py-2 mt-3">
                L’IA n’a pas répondu à temps : des questions ont été préparées à partir de votre texte. Corrigez-les si besoin.
              </p>
            )}

            {/* Aperçu des questions générées */}
            {drafts.length > 0 && (
              <div className="mt-3">
                <p className="text-[11px] text-foreground-500 mb-2">
                  {drafts.length} {t('cards.generated')}
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-56 overflow-y-auto pr-1">
                  {drafts.map((draft, index) => (
                    <div key={draft.id} className="relative rounded-lg border border-background-200/60 bg-background-50 p-2.5">
                      <p className="text-[10px] font-semibold text-foreground-400 mb-1">
                        {t('cards.question')} {index + 1}
                      </p>
                      <p className="text-[11px] font-medium text-foreground-900 line-clamp-2">{draft.question}</p>
                      <p className="text-[10px] text-foreground-600 mt-1 line-clamp-2">
                        <i className="ri-check-line text-accent-600 mr-0.5"></i>
                        {draft.answer}
                      </p>
                      <button
                        onClick={() => removeDraft(draft.id)}
                        className="absolute top-1.5 right-1.5 w-5 h-5 rounded flex items-center justify-center text-foreground-300 hover:text-red-600 cursor-pointer"
                        title={t('app.delete')}
                      >
                        <i className="ri-close-line text-[10px]"></i>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        <div className="flex items-center gap-2 mt-4">
          <button
            onClick={onClose}
            className="px-4 py-2.5 rounded-full text-xs font-medium text-foreground-600 hover:bg-background-100 cursor-pointer whitespace-nowrap"
          >
            {t('app.cancel')}
          </button>
          <div className="flex-1"></div>
          {drafts.length === 0 ? (
            <button
              onClick={generate}
              disabled={generating || !sourceText.trim()}
              className="px-4 py-2.5 rounded-full bg-foreground-950 text-background-50 text-xs font-semibold whitespace-nowrap cursor-pointer hover:bg-foreground-800 disabled:opacity-50 flex items-center gap-2"
            >
              {generating ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-sparkling-line"></i>}
              {generating ? t('cards.generating') : t('cards.generate')}
            </button>
          ) : (
            <button
              onClick={createDeck}
              className="px-4 py-2.5 rounded-full bg-accent-500 text-background-50 text-xs font-semibold whitespace-nowrap cursor-pointer hover:bg-accent-600 flex items-center gap-2"
            >
              <i className="ri-add-line"></i>
              {t('cards.newDeck')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}